import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebaseService';

export interface DateRange {
  startDate: Date;
  endDate: Date;
  numDays: number;
  duration?: string;
  price?: string;
  displayTitle?: string; // Enhanced: AI-extracted title
  url?: string;          // Enhanced: AI-extracted URL
  description?: string;  // Enhanced: AI-extracted description
  originalText?: string;
  sourceUrl?: string;    // Enhanced: Source URL this session was extracted from
  sourceIndex?: number;  // Enhanced: Index of the source URL (1-based)
}

// Enhanced interface for AI-powered session date fetching with multiple URL support
export interface EnhancedSessionDatesFetchResponse {
  success: boolean;
  dateRanges: DateRange[];
  processingMetrics?: {
    originalCharCount: number;
    sanitizedCharCount: number;
    patternsApplied: number;
    contentReductionPercent: number;
    timeElapsedMs: number;
  };
  debugInfo?: {
    processedHtmlContent: string;
    originalBodyContent: string;
    savedPatterns: string[];
    systemPrompt: string;    // Enhanced: Final resolved AI system prompt (with real values)
    systemPromptTemplate?: string; // Enhanced: Original template with placeholders
    aiResponse: string;      // Enhanced: Raw AI response
    processingMethod: string;
    explanation: string;
  };
  urlResults?: Array<{     // Enhanced: Results per URL processed
    url: string;
    success: boolean;
    rangesFound: number;
    error?: string;
  }>;
  error?: string;
}

/**
 * Enhanced AI-powered session date extraction with individual URL processing for real-time progress
 */
export const fetchEnhancedSessionDatesFromURL = async (
  dataFetchUrl: string,
  programName: string,
  locationId: string,
  onProgress?: (progress: {
    currentUrl: number;
    totalUrls: number;
    url: string;
    status: 'processing' | 'completed' | 'failed';
    result?: {
      success: boolean;
      dateRanges: DateRange[];
      error?: string;
    };
  }) => void
): Promise<EnhancedSessionDatesFetchResponse> => {
  console.log(`🚀 [Enhanced AI Data Fetch] STARTING PROGRESSIVE PROCESS`);
  console.log(`📍 Program: ${programName}`);
  console.log(`🏢 Location ID: ${locationId}`);
  console.log(`🔗 Raw URL input: ${dataFetchUrl}`);
  
  // Parse URLs to show user what we're about to process
  const urls = dataFetchUrl.split(/[\s\n\r\t]+/).map(url => url.trim()).filter(url => url.length > 0);
  console.log(`📊 [Enhanced AI Data Fetch] Detected ${urls.length} URL(s) for individual processing:`);
  urls.forEach((url, index) => {
    console.log(`   ${index + 1}. ${url}`);
  });

  if (urls.length === 0) {
    console.error(`❌ [Enhanced AI Data Fetch] No URLs found to process`);
    return {
      success: false,
      dateRanges: [],
      error: 'No valid URLs found in dataFetchUrl field'
    };
  }

  try {
    console.log(`⚙️ [Enhanced AI Data Fetch] Initializing Firebase Functions for ${urls.length} individual calls...`);
    
    // Initialize Firebase Functions with us-east4 region
    const functions = getFunctions(app, "us-east4");
    const fetchSessionDates = httpsCallable(functions, 'fetchSessionDatesFromURL');

    // Track results from all URLs
    const allDateRanges: DateRange[] = [];
    const urlResults: Array<{
      url: string;
      success: boolean;
      rangesFound: number;
      error?: string;
    }> = [];
    
    let totalOriginalChars = 0;
    let totalSanitizedChars = 0;
    let totalPatternsApplied = 0;
    let totalProcessingTime = 0;
    
    // Consolidated debug info from all URLs
    let consolidatedDebugInfo = {
      processedHtmlContent: '',
      originalBodyContent: '',
      savedPatterns: [] as string[],
      systemPrompt: '',
      systemPromptTemplate: '',
      aiResponse: '',
      processingMethod: `Individual URL Processing - ${urls.length} separate calls`,
      explanation: `Processing ${urls.length} URL${urls.length !== 1 ? 's' : ''} individually with real-time progress updates`
    };

    // Process each URL individually
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      const urlStartTime = Date.now();
      
      console.log(`🌐 [Enhanced AI Data Fetch] Starting individual call ${i + 1}/${urls.length} for: ${url.substring(0, 100)}${url.length > 100 ? '...' : ''}`);
      
      // Notify progress callback - starting URL
      if (onProgress) {
        onProgress({
          currentUrl: i + 1,
          totalUrls: urls.length,
          url,
          status: 'processing'
        });
      }

      try {
        console.log(`🔥 [Enhanced AI Data Fetch] Calling Cloud Function for URL ${i + 1}/${urls.length}...`);
        
        // Call Cloud Function for this single URL
        const result = await fetchSessionDates({
          dataFetchUrl: url, // Single URL only
          programName,
          locationId
        });

        const urlEndTime = Date.now();
        const urlProcessingTime = urlEndTime - urlStartTime;

        console.log(`📥 [Enhanced AI Data Fetch] URL ${i + 1}/${urls.length} Cloud Function completed in ${urlProcessingTime}ms`);

        const data = result.data as {
          success: boolean;
          dateRanges: Array<{
            startDate: string;
            endDate: string;
            numDays: number;
            duration?: string;
            price?: string;
            displayTitle?: string;
            url?: string;
            description?: string;
            originalText?: string;
            sourceUrl?: string;
            sourceIndex?: number;
          }>;
          processingMetrics?: {
            originalCharCount: number;
            sanitizedCharCount: number;
            patternsApplied: number;
            contentReductionPercent: number;
            timeElapsedMs: number;
          };
          debugInfo?: {
            processedHtmlContent: string;
            originalBodyContent: string;
            savedPatterns: string[];
            systemPrompt: string;
            systemPromptTemplate?: string;
            aiResponse: string;
            processingMethod: string;
            explanation: string;
          };
          error?: string;
        };

        console.log(`📊 [Enhanced AI Data Fetch] URL ${i + 1}/${urls.length} results:`, {
          success: data.success,
          dateRangesFound: data.dateRanges?.length || 0,
          error: data.error || 'None'
        });

        if (data.success && data.dateRanges && data.dateRanges.length > 0) {
          // Convert date strings to Date objects and add source URL info
          const convertedRanges = data.dateRanges.map((range) => {
            const parseLocalDate = (dateStr: string): Date => {
              const [year, month, day] = dateStr.split('-').map(Number);
              return new Date(year, month - 1, day);
            };

            return {
              startDate: parseLocalDate(range.startDate),
              endDate: parseLocalDate(range.endDate),
              numDays: range.numDays,
              duration: range.duration,
              price: range.price,
              displayTitle: range.displayTitle,
              url: range.url,
              description: range.description,
              originalText: range.originalText,
              sourceUrl: url, // Current URL being processed
              sourceIndex: i + 1 // 1-based index
            };
          });

          allDateRanges.push(...convertedRanges);
          console.log(`✅ [Enhanced AI Data Fetch] URL ${i + 1}/${urls.length} SUCCESS - Added ${convertedRanges.length} date ranges`);
        } else {
          console.log(`⚠️ [Enhanced AI Data Fetch] URL ${i + 1}/${urls.length} returned no date ranges`);
        }

        // Track URL result
        urlResults.push({
          url,
          success: data.success,
          rangesFound: data.dateRanges?.length || 0,
          error: data.error
        });

        // Accumulate metrics
        if (data.processingMetrics) {
          totalOriginalChars += data.processingMetrics.originalCharCount;
          totalSanitizedChars += data.processingMetrics.sanitizedCharCount;
          totalPatternsApplied += data.processingMetrics.patternsApplied;
          totalProcessingTime += data.processingMetrics.timeElapsedMs;
        }

        // Consolidate debug info (use info from last successful URL)
        if (data.success && data.debugInfo) {
          consolidatedDebugInfo = {
            ...consolidatedDebugInfo,
            savedPatterns: data.debugInfo.savedPatterns,
            systemPromptTemplate: data.debugInfo.systemPromptTemplate,
            // Append HTML content and AI responses from all URLs
            processedHtmlContent: consolidatedDebugInfo.processedHtmlContent + 
              `\n\n=== URL ${i + 1}: ${url} ===\n` + data.debugInfo.processedHtmlContent,
            originalBodyContent: consolidatedDebugInfo.originalBodyContent + 
              `\n\n=== URL ${i + 1}: ${url} ===\n` + data.debugInfo.originalBodyContent,
            systemPrompt: data.debugInfo.systemPrompt,
            aiResponse: consolidatedDebugInfo.aiResponse + 
              `\n\n=== URL ${i + 1}: ${url} ===\n` + data.debugInfo.aiResponse
          };
        }

        // Notify progress callback - completed URL
        if (onProgress) {
          onProgress({
            currentUrl: i + 1,
            totalUrls: urls.length,
            url,
            status: 'completed',
            result: {
              success: data.success,
              dateRanges: data.dateRanges?.map((range) => {
                const parseLocalDate = (dateStr: string): Date => {
                  const [year, month, day] = dateStr.split('-').map(Number);
                  return new Date(year, month - 1, day);
                };
                return {
                  startDate: parseLocalDate(range.startDate),
                  endDate: parseLocalDate(range.endDate),
                  numDays: range.numDays,
                  duration: range.duration,
                  price: range.price,
                  displayTitle: range.displayTitle,
                  url: range.url,
                  description: range.description,
                  originalText: range.originalText,
                  sourceUrl: url,
                  sourceIndex: i + 1
                };
              }) || [],
              error: data.error
            }
          });
        }

      } catch (urlError) {
        const urlEndTime = Date.now();
        const urlProcessingTime = urlEndTime - urlStartTime;
        
        console.error(`❌ [Enhanced AI Data Fetch] URL ${i + 1}/${urls.length} FAILED after ${urlProcessingTime}ms:`, urlError);
        
        if (urlError instanceof Error) {
          console.error(`   Error name: ${urlError.name}`);
          console.error(`   Error message: ${urlError.message}`);
        }

        // Track failed URL result
        urlResults.push({
          url,
          success: false,
          rangesFound: 0,
          error: urlError instanceof Error ? urlError.message : 'Unknown error'
        });

        // Notify progress callback - failed URL
        if (onProgress) {
          onProgress({
            currentUrl: i + 1,
            totalUrls: urls.length,
            url,
            status: 'failed',
            result: {
              success: false,
              dateRanges: [],
              error: urlError instanceof Error ? urlError.message : 'Unknown error'
            }
          });
        }
      }
    }

    const successfulUrls = urlResults.filter(r => r.success).length;
    const failedUrls = urlResults.filter(r => !r.success).length;
    
    console.log(`🏁 [Enhanced AI Data Fetch] PROGRESSIVE PROCESSING COMPLETE`);
    console.log(`📊 Final Results:`, {
      totalUrls: urls.length,
      successfulUrls,
      failedUrls,
      totalDateRanges: allDateRanges.length,
      totalProcessingTime: `${totalProcessingTime}ms`
    });

    // Create comprehensive explanation for debug info
    const urlSummary = urlResults.map((result, index) => 
      `URL ${index + 1}: ${result.success ? `✓ ${result.rangesFound} sessions` : `✗ ${result.error}`}`
    ).join('\n');
    
    consolidatedDebugInfo.explanation = `Processed ${urls.length} URL${urls.length !== 1 ? 's' : ''} individually:\n${urlSummary}`;

    return {
      success: allDateRanges.length > 0,
      dateRanges: allDateRanges,
      processingMetrics: {
        originalCharCount: totalOriginalChars,
        sanitizedCharCount: totalSanitizedChars,
        patternsApplied: totalPatternsApplied,
        contentReductionPercent: totalOriginalChars > 0 ? 
          ((totalOriginalChars - totalSanitizedChars) / totalOriginalChars) * 100 : 0,
        timeElapsedMs: totalProcessingTime
      },
      debugInfo: consolidatedDebugInfo,
      urlResults: urlResults
    };

  } catch (error) {
    console.error(`❌ [Enhanced AI Data Fetch] CRITICAL ERROR in progressive processing:`, error);
    
    if (error instanceof Error) {
      console.error(`   Error name: ${error.name}`);
      console.error(`   Error message: ${error.message}`);
      console.error(`   Error stack: ${error.stack}`);
    }

    return {
      success: false,
      dateRanges: [],
      debugInfo: {
        processedHtmlContent: '',
        originalBodyContent: '',
        savedPatterns: [],
        systemPrompt: '',
        aiResponse: '',
        processingMethod: 'ERROR - Progressive Processing Failed',
        explanation: `Critical error during progressive URL processing: ${error instanceof Error ? error.message : String(error)}`
      },
      error: error instanceof Error ? error.message : 'Failed to fetch session dates'
    };
  }
};

/**
 * Extract readable text content from HTML
 */
function extractTextFromHTML(html: string): string {
  // Remove script and style elements
  let cleaned = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  cleaned = cleaned.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
  
  // Remove HTML tags
  cleaned = cleaned.replace(/<[^>]*>/g, ' ');
  
  // Decode HTML entities
  cleaned = cleaned.replace(/&nbsp;/g, ' ');
  cleaned = cleaned.replace(/&amp;/g, '&');
  cleaned = cleaned.replace(/&lt;/g, '<');
  cleaned = cleaned.replace(/&gt;/g, '>');
  cleaned = cleaned.replace(/&quot;/g, '"');
  cleaned = cleaned.replace(/&#39;/g, "'");
  
  // Clean up whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  return cleaned;
}

/**
 * AI-powered date range extraction from text content
 */
function extractDateRanges(text: string, programName?: string): DateRange[] {
  const dateRanges: DateRange[] = [];
  
  console.log(`[AI Date Extract] Analyzing text for program: ${programName}`);
  
  // Pattern 1: "Month DD-DD" (e.g., "June 16-20", "July 8-12")
  const monthRangePattern = /\b(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s*[-–—]\s*(\d{1,2})\b/gi;
  
  // Pattern 2: "Month DD - Month DD" (e.g., "June 16 - June 20", "Dec 23 - Jan 5")
  const fullDateRangePattern = /\b(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s*[-–—]\s*(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\b/gi;
  
  // Pattern 3: "Month DD - DD" with year context
  const monthRangeWithYearPattern = /\b(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s*[-–—]\s*(\d{1,2}),?\s*(\d{4})\b/gi;

  const currentYear = new Date().getFullYear();
  
  // Try Pattern 1: Month DD-DD
  let match;
  while ((match = monthRangePattern.exec(text)) !== null) {
    const [fullMatch, monthStr, startDay, endDay] = match;
    
    try {
      const month = getMonthNumber(monthStr.toLowerCase());
      const startDate = new Date(currentYear, month, parseInt(startDay));
      const endDate = new Date(currentYear, month, parseInt(endDay));
      
      if (isValidDateRange(startDate, endDate)) {
        const numDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        dateRanges.push({ startDate, endDate, numDays });
        console.log(`[AI Date Extract] Found range: ${fullMatch} -> ${startDate.toDateString()} to ${endDate.toDateString()}`);
      }
    } catch (error) {
      console.warn(`[AI Date Extract] Failed to parse: ${fullMatch}`, error);
    }
  }
  
  // Reset regex lastIndex for next pattern
  fullDateRangePattern.lastIndex = 0;
  
  // Try Pattern 2: Month DD - Month DD
  while ((match = fullDateRangePattern.exec(text)) !== null) {
    const [fullMatch, startMonthStr, startDay, endMonthStr, endDay] = match;
    
    try {
      const startMonth = getMonthNumber(startMonthStr.toLowerCase());
      const endMonth = getMonthNumber(endMonthStr.toLowerCase());
      
      const startYear = currentYear;
      let endYear = currentYear;
      
      // Handle year crossing (e.g., Dec 23 - Jan 5)
      if (endMonth < startMonth) {
        endYear = currentYear + 1;
      }
      
      const startDate = new Date(startYear, startMonth, parseInt(startDay));
      const endDate = new Date(endYear, endMonth, parseInt(endDay));
      
      if (isValidDateRange(startDate, endDate)) {
        const numDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        dateRanges.push({ startDate, endDate, numDays });
        console.log(`[AI Date Extract] Found range: ${fullMatch} -> ${startDate.toDateString()} to ${endDate.toDateString()}`);
      }
    } catch (error) {
      console.warn(`[AI Date Extract] Failed to parse: ${fullMatch}`, error);
    }
  }
  
  // Remove duplicates (dates within 1 day of each other)
  const uniqueRanges = removeDuplicateDateRanges(dateRanges);
  
  console.log(`[AI Date Extract] Final result: ${uniqueRanges.length} unique date ranges`);
  
  return uniqueRanges;
}

/**
 * Convert month name to number (0-based)
 */
function getMonthNumber(monthName: string): number {
  const monthMap: { [key: string]: number } = {
    'january': 0, 'jan': 0,
    'february': 1, 'feb': 1,
    'march': 2, 'mar': 2,
    'april': 3, 'apr': 3,
    'may': 4,
    'june': 5, 'jun': 5,
    'july': 6, 'jul': 6,
    'august': 7, 'aug': 7,
    'september': 8, 'sep': 8, 'sept': 8,
    'october': 9, 'oct': 9,
    'november': 10, 'nov': 10,
    'december': 11, 'dec': 11
  };
  
  return monthMap[monthName.toLowerCase()] ?? -1;
}

/**
 * Validate that a date range makes sense
 */
function isValidDateRange(startDate: Date, endDate: Date): boolean {
  return startDate instanceof Date && 
         endDate instanceof Date && 
         !isNaN(startDate.getTime()) && 
         !isNaN(endDate.getTime()) && 
         startDate <= endDate &&
         endDate.getTime() - startDate.getTime() < (365 * 24 * 60 * 60 * 1000); // Less than 1 year
}

/**
 * Remove duplicate date ranges
 */
function removeDuplicateDateRanges(ranges: DateRange[]): DateRange[] {
  const unique: DateRange[] = [];
  
  for (const range of ranges) {
    const isDuplicate = unique.some(existing => 
      Math.abs(existing.startDate.getTime() - range.startDate.getTime()) < (2 * 24 * 60 * 60 * 1000) &&
      Math.abs(existing.endDate.getTime() - range.endDate.getTime()) < (2 * 24 * 60 * 60 * 1000)
    );
    
    if (!isDuplicate) {
      unique.push(range);
    }
  }
  
  return unique.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
} 